//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { TrashCan } from '@carbon/icons-react';
import { useUserAuth } from '../../UserAuth';
import { useTeamspaceContext } from '../../TeamspaceContext';
import TenantDataService from '../../utils/TenantDataService';
import {
  AnnouncementDto,
  CreateAnnouncementRequest,
  TeamspaceIdentifier,
} from '../Sidebar/SidebarTypes';
import CreateAnnouncementPanel from './CreateAnnouncementPanel';
import { ANNOUNCEMENTS_CHANGED_EVENT } from '../../constants/announcementEvents';
import CustomSnackbar, { useSnackbar } from '../common/CustomSnackbar';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import announcementsMock from '../../mocks/announcementsMock.json';

const Announcements: React.FC = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useUserAuth();
  const { teamspaceId } = useTeamspaceContext();

  // Stable ref for `t` so fetchAnnouncements identity doesn't change when
  // react-i18next re-initialises the translation function.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const tenantDataService = useMemo(
    () =>
      new TenantDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );

  const [announcements, setAnnouncements] = useState<AnnouncementDto[]>([]);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userTeamspaces, setUserTeamspaces] = useState<TeamspaceIdentifier[]>(
    [],
  );
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();

  // Monotonically increasing fetch id — lets us discard results from stale
  // in-flight requests so they don't overwrite newer data or flash error toasts.
  const fetchIdRef = useRef(0);

  const fetchAnnouncements = useCallback(async () => {
    if (!teamspaceId) return;
    const id = ++fetchIdRef.current;
    setIsLoading(true);
    try {
      if (process.env.NODE_ENV === 'development') {
        if (id === fetchIdRef.current) {
          setAnnouncements(
            announcementsMock.announcements as AnnouncementDto[],
          );
        }
      } else {
        const result =
          await tenantDataService.getActiveAnnouncements(teamspaceId);
        if (id === fetchIdRef.current) {
          setAnnouncements(result ?? []);
        }
      }
    } catch {
      // Only show the error toast if this is still the latest fetch; stale
      // responses from superseded requests are silently discarded.
      if (id === fetchIdRef.current) {
        showSnackbar(tRef.current('announcement-error-fetching'), 'error');
      }
    } finally {
      if (id === fetchIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [teamspaceId, tenantDataService, showSnackbar]);

  const fetchTeamspaces = useCallback(async () => {
    try {
      const registerResponse = await tenantDataService.registerUser();
      const userTeamspacesResp = await tenantDataService.getUserTeamspaces(
        registerResponse?._links?.user?.href,
      );
      const roles = userTeamspacesResp?._embedded?.teamspaceRoles ?? [];
      const teamspaceList: TeamspaceIdentifier[] = [];
      await Promise.all(
        roles.map(async (ts) => {
          const urlParts = ts?._links?.teamspace?.href?.split('/rest');
          if (urlParts?.[1]) {
            const tsData = await tenantDataService.invokeHREF(urlParts[1]);
            teamspaceList.push({
              name: tsData?.name,
              id: tsData?.id,
              slug: tsData?.slug,
              role: ts?.role ?? '',
            });
          }
        }),
      );
      setUserTeamspaces(teamspaceList);
    } catch {
      // silently ignore — teamspace list is non-critical for page render
    }
  }, [tenantDataService]);

  useEffect(() => {
    if (
      process.env.NODE_ENV === 'development' ||
      (isAuthenticated && user?.access_token && teamspaceId)
    ) {
      fetchAnnouncements();
      if (isAuthenticated && user?.access_token && teamspaceId) {
        fetchTeamspaces();
      }
    }
  }, [
    isAuthenticated,
    user?.access_token,
    teamspaceId,
    fetchAnnouncements,
    fetchTeamspaces,
  ]);

  const handleCreate = useCallback(
    async (tsId: string, request: CreateAnnouncementRequest) => {
      try {
        await tenantDataService.createAnnouncement(tsId, request);
        showSnackbar(
          tRef.current('announcement-created-successfully'),
          'success',
        );
        setIsCreatePanelOpen(false);
        // Short delay so the backend has time to commit before we re-query.
        await new Promise((r) => setTimeout(r, 500));
        fetchAnnouncements();
        window.dispatchEvent(new Event(ANNOUNCEMENTS_CHANGED_EVENT));
      } catch {
        showSnackbar(tRef.current('announcement-error-creating'), 'error');
      }
    },
    [tenantDataService, showSnackbar, fetchAnnouncements],
  );

  const handleDelete = useCallback(
    async (announcementId: string) => {
      if (!teamspaceId) return;
      try {
        await tenantDataService.deleteAnnouncement(teamspaceId, announcementId);
        showSnackbar(
          tRef.current('announcement-deleted-successfully'),
          'success',
        );
        await new Promise((r) => setTimeout(r, 500));
        fetchAnnouncements();
        window.dispatchEvent(new Event(ANNOUNCEMENTS_CHANGED_EVENT));
      } catch {
        showSnackbar(tRef.current('announcement-error-deleting'), 'error');
      }
    },
    [teamspaceId, tenantDataService, showSnackbar, fetchAnnouncements],
  );

  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-CA');
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="flex h-full flex-col" data-testid="announcements-page">
      {/* Content Area */}
      <div className="flex-1 overflow-auto bg-[#F4F4F4] px-4 pt-4 dark:bg-[#161616]">
        <div className="rounded-lg bg-white p-6 dark:bg-[#262626]">
          {/* Heading + Add Button */}
          <div className="mb-6 flex items-center justify-between">
            <h2
              className="text-base font-medium text-[#4a4a56] dark:text-[#c8c8dd]"
              data-testid="announcements-heading"
            >
              {t('manage-announcements')}
            </h2>
            <button
              onClick={() => setIsCreatePanelOpen(true)}
              className="rounded bg-[#0F62FE] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#0353E9] dark:bg-[#0F62FE] dark:hover:bg-[#4589ff]"
              data-testid="add-announcement-button"
            >
              {t('add-announcement')}
            </button>
          </div>

          {/* Announcements Table */}
          {isLoading && (
            <div
              className="py-8 text-center"
              data-testid="announcements-loading"
            >
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('loading-trans')}
              </p>
            </div>
          )}

          {!isLoading && announcements.length === 0 && (
            <div
              className="py-8 text-center"
              data-testid="no-announcements-message"
            >
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('announcement-no-announcements')}
              </p>
            </div>
          )}

          {!isLoading && announcements.length > 0 && (
            <div className="table-container" data-testid="announcements-list">
              <Table>
                <TableHeader className="table-header">
                  <TableRow className="hover:bg-transparent">
                    <TableHead>{t('announcement-title')}</TableHead>
                    <TableHead>{t('announcement-content')}</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>Start Time</TableHead>
                    <TableHead>End Date</TableHead>
                    <TableHead>End Time</TableHead>
                    <TableHead>{t('announcement-scope')}</TableHead>
                    <TableHead>{t('action')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {announcements.map((announcement) => (
                    <TableRow
                      key={announcement.id}
                      className="table-row"
                      data-testid={`announcement-card-${announcement.id}`}
                    >
                      <TableCell className="table-cell text-sm font-medium">
                        {announcement.title}
                      </TableCell>
                      <TableCell className="table-cell max-w-xs truncate text-sm">
                        {announcement.message}
                      </TableCell>
                      <TableCell className="table-cell text-sm">
                        <span
                          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
                            announcement.type === 'maintenance'
                              ? 'bg-orange-100 text-orange-800 dark:bg-orange-500/10 dark:text-orange-300'
                              : announcement.type === 'update'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-500/10 dark:text-blue-300'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {announcement.type?.charAt(0).toUpperCase() +
                            announcement.type?.slice(1) || 'General'}
                        </span>
                      </TableCell>
                      <TableCell className="table-cell text-sm">
                        {formatDate(announcement.startsAt)}
                      </TableCell>
                      <TableCell className="table-cell text-sm">
                        {formatTime(announcement.startsAt)}
                      </TableCell>
                      <TableCell className="table-cell text-sm">
                        {formatDate(announcement.expiresAt)}
                      </TableCell>
                      <TableCell className="table-cell text-sm">
                        {formatTime(announcement.expiresAt)}
                      </TableCell>
                      <TableCell className="table-cell text-sm">
                        {announcement.scope === 'GLOBAL'
                          ? t('announcement-scope-global')
                          : t('announcement-scope-teamspace')}
                      </TableCell>
                      <TableCell className="table-cell">
                        <button
                          onClick={() => handleDelete(announcement.id)}
                          className="inline-flex size-8 items-center justify-center rounded text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                          data-testid={`delete-announcement-${announcement.id}`}
                          title={t('delete')}
                        >
                          <TrashCan size={16} />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* Create Panel */}
      <CreateAnnouncementPanel
        isOpen={isCreatePanelOpen}
        onClose={() => setIsCreatePanelOpen(false)}
        onSubmit={handleCreate}
        teamspaces={userTeamspaces}
        currentTeamspaceId={teamspaceId}
      />

      {/* Snackbar */}
      <CustomSnackbar
        open={snackbarState.open}
        message={snackbarState.message}
        severity={snackbarState.severity}
        onClose={closeSnackbar}
      />
    </div>
  );
};

export default Announcements;
